import React from 'react';
import { connect } from 'react-redux';
import { Redirect, Route, RouteComponentProps, Switch } from 'react-router-dom';

import { selectPreviousUrl } from '~selectors/NavigationStateSelectors';
import { UrlTelemetryService } from '~src/ts/services/UrlTelemetryService';

import { RevisionHistoryInfo } from '../../app/spaces/RevisionHistorySpace/RevisionHistorySpaceProvider';
import { RevisionHistorySpaceUrlUpdater } from '../../app/spaces/RevisionHistorySpace/RevisionHistorySpaceUrlUpdater';
import { RouterError } from '../../app/spaces/RouterHelpers/RouterError';
import { SpacesSiteUrls } from '../../app/spaces/SpacesSiteUrls';
import { AppState } from '../../app/state/AppState';
import { WorkbookSubspaceName } from '../../app/state/SpaceInfo';
import { MapStateToProps } from '../../app/utils/ConnectUtils';
import { selectCatalogingOnSiteEnabled } from '../../catalog/selectors/CatalogSelectors';
import { FeatureFlags } from '../../ts/FeatureFlags';
import { WorkbookSpaceProvider, WorkbookSpaceProviderProps } from './WorkbookSpaceProvider';

export interface SpaceRouteParams {
  readonly id: string;
}

export interface WorkbookRouteComponentProps extends RouteComponentProps<SpaceRouteParams> {}

export interface RevisionHistoryRouteComponentProps extends RouteComponentProps<RevisionHistoryInfo> {}

export interface WorkbookSpaceRouterOwnProps {
  readonly siteName: string;
  readonly urls: SpacesSiteUrls;
}

export interface WorkbookSpaceRouterStateProps {
  readonly previousUrl: string;
  readonly catalogingOnSiteEnabled: boolean;
}

export type WorkbookSpaceRouterProps = WorkbookRouteComponentProps & WorkbookSpaceRouterOwnProps & WorkbookSpaceRouterStateProps;

export class WorkbookSpaceRouterComponent extends React.Component<WorkbookSpaceRouterProps> {
  public render() {
    return (
      <Switch>
        <Route exact path={this.props.urls.workbookViewsPath(':id')} render={this.workbookSpaceProvider('views')} />
        <Route exact path={this.props.urls.workbookDatasourcesPath(':id')} render={this.workbookSpaceProvider('datasources')} />
        {
          FeatureFlags.metricsSpaceEnabled &&
          <Route exact path={this.props.urls.workbookConnectedMetricsPath(':id')} render={this.workbookSpaceProvider('connectedMetrics')} />
        }
        <Route exact path={this.props.urls.workbookCustomViewsPath(':id')} render={this.workbookSpaceProvider('customViews')} />
        <Route exact path={this.props.urls.workbookSubscriptionsPath(':id')} render={this.workbookSpaceProvider('subscriptions')} />
        <Route exact path={this.props.urls.workbookExtractRefreshesPath(':id')} render={this.workbookSpaceProvider('extractTasks')} />
        {
          FeatureFlags.workbookAccelerationUIEnabled &&
          <Route exact path={this.props.urls.workbookAccelerationsPath(':id')} render={this.workbookSpaceProvider('workbookAccelerations')} />
        }
        {this.getLineageRoute()}
        <Route exact path={this.props.urls.workbookPublishresultPath(':id')} render={({ match }) => (
          <Redirect to={this.props.urls.workbookViewsPath(match.params.id)} />
        )} />
        <Route exact path={this.props.urls.workbookDetailsPath(':id')} render={({ match }) => (
          <Redirect to={this.props.urls.workbookViewsPath(match.params.id)} />
        )} />
        <Route path={this.props.urls.workbookPath(':id')} render={({ match }) => {
          // Query parameters are removed by redirect, so telemetry which relies
          // on them must be sent before.
          UrlTelemetryService.maybeSendDataQualitySubscriptionEvent(match.params.id, 'workbook');
          return (
            <Switch>
              {/* revision history paths */}
              <Route path={this.props.urls.workbookRevisionHistoryPath({workbookUrlId: ':workbookUrlId', revisionNumber: ':revisionNumber'})} render={this.revisionHistoryProvider()} />
              <Route path={this.props.urls.workbookRevisionHistorySpecificSheetPath({workbookUrlId: ':workbookUrlId', sheetUrlId: ':sheetUrlId', revisionNumber: ':revisionNumber'})} render={this.revisionHistoryProvider()} />
              {/* default redirect  */}
              <Redirect to={this.props.urls.workbookViewsPath(match.params.id)} />
            </Switch>
          );
        }} />
        <Route render={this.error} />
      </Switch>
    );
  }

  private getLineageRoute = (): JSX.Element => {
    if (this.props.catalogingOnSiteEnabled) {
      return <Route path={this.props.urls.workbookLineagePath(':id')} render={this.workbookSpaceProvider('lineage')} />;
    } else {
      return (
        // we want to match on any workbook path starting with /lineage so don't use 'exact' on the route
        <Route path={this.props.urls.workbookLineagePath(':id')} render={({ match }) => (
          <Redirect to={this.props.urls.workbookViewsPath(match.params.id)} />
        )} />);
    }
  }

  private workbookSpaceProvider = (subspaceName: WorkbookSubspaceName): (props: WorkbookRouteComponentProps) => JSX.Element => {
    return (props: WorkbookRouteComponentProps): JSX.Element => {
      const workbookId = props.match.params.id;
      const publishResultDisplay = this.props.previousUrl.includes(this.props.urls.workbookPublishresultPath(workbookId));
      const workbookSpaceProviderProps: WorkbookSpaceProviderProps = {
        siteName: this.props.siteName,
        subspaceName,
        publishResultDisplay,
        publishResultUrl: publishResultDisplay ? this.props.previousUrl : '',
        routeLocationSearch: props.location.search,
        routeLocationPath: props.location.pathname,
        workbookId,
        urls: this.props.urls
      };

      return <WorkbookSpaceProvider {...workbookSpaceProviderProps} />;
    };
  }

  private revisionHistoryProvider = (): (props: RevisionHistoryRouteComponentProps) => JSX.Element => {
    return (props: RevisionHistoryRouteComponentProps): JSX.Element => {
      const revisionHistoryInfo: RevisionHistoryInfo = {
        workbookUrlId: props.match.params.workbookUrlId,
        revisionNumber: props.match.params.revisionNumber,
        sheetUrlId: props.match.params.sheetUrlId
      };
      const renderRevisionHistorySpaceProps: RevisionHistorySpaceUrlUpdater.OwnProps = {
        revisionHistoryInfo,
        routeLocationSearch: props.location.search,
        urls: this.props.urls,
        renderProvider: RevisionHistorySpaceUrlUpdater.renderProvider
      };

      return <RevisionHistorySpaceUrlUpdater.ConnectedContainer {...renderRevisionHistorySpaceProps} />;
    };
  }

  private error = (): JSX.Element => {
    return <RouterError errorType={'default'} urls={this.props.urls}/>;
  }
}

const mapStateToProps: MapStateToProps<WorkbookSpaceRouterStateProps> = (state: AppState) => ({
  previousUrl: selectPreviousUrl(state),
  catalogingOnSiteEnabled: selectCatalogingOnSiteEnabled(state)
});

// Component must *not* be pure as to allow route updates to flow through
export const WorkbookSpaceRouter: React.ComponentClass<WorkbookSpaceRouterOwnProps>
  = connect(mapStateToProps, null, null, {pure: false})(WorkbookSpaceRouterComponent);
